import { useState, useRef, useCallback, useEffect } from "react";
import { doc, setDoc } from "firebase/firestore";
import { db, auth } from "../firebase";
import { CATEGORIES, COST_RANGE_OPTIONS } from "./constants";
import Img from "./Img";
import ImageEditor from "./ImageEditor";
import Toast, { useToast } from "./Toast";
import { COLOR, FONT } from "../utils/theme";
import { Icon } from "../utils/icons";
import useWindowWidth from "../utils/useWindowWidth";
import { getCostRange } from "../utils/costHelpers";
import { formatCost } from "../utils/formatCost";
import { takeSnapshot } from "../utils/snapshots";
import { isDuplicate } from "../utils/dedup";
import { getSetting } from "../utils/settings";
import { authenticatedFetch } from "../utils/apiClient";
import { resolveDisplayPref } from "../utils/displayPrefs";

const API_URL = process.env.REACT_APP_PROXY_URL || 'http://localhost:3001';

const TIME_OPTIONS = ["Early AM", "Morning", "Midday", "Afternoon", "Evening", "Late Night"];
const CATEGORY_OPTIONS = Object.entries(CATEGORIES).map(([value, { label }]) => ({
  value,
  label,
}));

const COST_TYPE_MAP = {
  free:          { category: null,            costRange: "free" },
  budget:        { category: null,            costRange: "$" },
  low:           { category: null,            costRange: "$" },
  mid:           { category: null,            costRange: "$$" },
  moderate:      { category: null,            costRange: "$$" },
  dining:        { category: "dining",        costRange: "$$" },
  flight:        { category: "transport",     costRange: "$$$" },
  accommodation: { category: "accommodation", costRange: "$$" },
  stay:          { category: "accommodation", costRange: "$$" },
};
function deriveFields(act) {
  const m = COST_TYPE_MAP[act.costType] || {};
  return {
    category:  act.category  || m.category  || "sightseeing",
    costRange: act.costRange || getCostRange(act.cost),
  };
}
// ⚠️ COLOR.inkMid (#8B7355) — URL-encoded as %238B7355 in SVG data URL.
// Cannot reference JS token inside CSS string. Update manually if inkMid changes.
const SELECT_ARROW = "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath d='M1 1l5 5 5-5' stroke='%238B7355' stroke-width='1.5' fill='none'/%3E%3C/svg%3E\")";

const labelStyle = {
  fontSize: 10, color: COLOR.inkMid, fontFamily: FONT.mono,
  letterSpacing: "0.1em", textTransform: "uppercase",
  marginBottom: 5, display: "block",
};
const inputStyle = {
  width: "100%", boxSizing: "border-box",
  background: COLOR.white, border: `1px solid ${COLOR.borderWarm}`,
  borderRadius: 8, padding: "10px 12px",
  color: COLOR.inkDark, fontSize: 14,
  fontFamily: FONT.serif,
  outline: "none", transition: "border-color 0.15s",
};
const selectStyle = {
  ...inputStyle, cursor: "pointer", appearance: "none",
  backgroundImage: SELECT_ARROW, backgroundRepeat: "no-repeat",
  backgroundPosition: "right 12px center", paddingRight: 32,
};
const focus = e => e.target.style.borderColor = COLOR.mauve;
const blur  = e => e.target.style.borderColor = COLOR.borderWarm;


export default function ActivityEditPanel({ act, onSave, onClose, onActivityUpdate, onDeleteActivity, otherActivityTitles = [], tripCities = [], tripData, day = null, mode = "view" }) {
  const isNew = !!act._isNew;
  const [closing, setClosing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [toast, showToast] = useToast();
  const [showImageEditor, setShowImageEditor] = useState(false);
  const [locked, setLocked] = useState(act.locked || false);
  const [lockSaving, setLockSaving] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentMode, setCurrentMode] = useState(mode);

  // Desktop detection
  const { width, isDesktop } = useWindowWidth();

  // Display pref flags (view-layer only — never strip from save payload)
  const showBooking = resolveDisplayPref('showBookingInfo', tripData);
  const showCost = resolveDisplayPref('showCostEstimates', tripData);
  const showLocation = resolveDisplayPref('showLocation', tripData);
  const latestPhotoRef = useRef(null);

  // ── Desktop resize state ─────────────────────────────────────────────────
  const [panelWidth, setPanelWidth] = useState(() => {
    const saved = localStorage.getItem('wandero_editpanel_width');
    return saved ? Number(saved) : null;
  });

  useEffect(() => {
    if (panelWidth === null) {
      localStorage.removeItem('wandero_editpanel_width');
    } else {
      localStorage.setItem('wandero_editpanel_width', panelWidth);
    }
  }, [panelWidth]);

  const resolvedWidth = isDesktop
    ? (panelWidth ?? (width - 80))
    : "100%";

  const handleResizeDragStart = (e) => {
    e.preventDefault();
    const startX = e.clientX;
    const startWidth = panelWidth ?? (width - 80);

    const onMove = (ev) => {
      const delta = startX - ev.clientX;
      const newWidth = Math.min(
        width - 80,
        Math.max(width * 0.40, startWidth + delta)
      );
      setPanelWidth(newWidth);
    };

    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  const handleResizeDoubleClick = () => setPanelWidth(null);

  // Lightbox Escape key
  useEffect(() => {
    if (!lightboxOpen) return;
    const handler = e => { if (e.key === "Escape") setLightboxOpen(false); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [lightboxOpen]);

  const handleToggleLock = async () => {
    if (lockSaving) return;
    const next = !locked;
    setLocked(next);
    setLockSaving(true);
    try {
      await setDoc(doc(db, "activities", act.id), { locked: next }, { merge: true });
      onActivityUpdate?.(act.id, { locked: next });
      showToast(next ? "Approved ✓" : "Unapproved", "success");
    } catch (e) {
      setLocked(!next);
      showToast("⚠️ Failed to update approval", "error");
    } finally {
      setLockSaving(false);
    }
  };

  const [form, setForm] = useState(() => {
    const derived = deriveFields(act);
    return {
      title:        act.title             || "",
      time:         act.time              || "Morning",
      duration:     act.duration          || "",
      cost:         act.cost              || "",
      category:     derived.category,
      costRange:    derived.costRange,
      costDetail:   act.costDetail        || "",
      bookingUrl:   act.bookingUrl        || "",
      bookingNote:  act.bookingNote       || "",
      detail:       act.detail            || "",
      locationName: act.location?.name    || "",
      locationAddr: act.location?.address || "",
      img:          act.img || act.photo  || "",
      imgCropParams:act.imgCropParams     || null,
    };
  });

  // Re-sync form state if act prop changes (e.g. panel reused for different activity)
  useEffect(() => {
    if (act?.id) {
      const derived = deriveFields(act);
      setForm({
        title:        act.title             || "",
        time:         act.time              || "Morning",
        duration:     act.duration          || "",
        cost:         act.cost              || "",
        category:     derived.category,
        costRange:    derived.costRange,
        costDetail:   act.costDetail        || "",
        bookingUrl:   act.bookingUrl        || "",
        bookingNote:  act.bookingNote       || "",
        detail:       act.detail            || "",
        locationName: act.location?.name    || "",
        locationAddr: act.location?.address || "",
        img:          act.img || act.photo  || "",
        imgCropParams:act.imgCropParams     || null,
      });
      setLocked(act.locked || false);
      latestPhotoRef.current = null;
      costRangeManualRef.current = false;
      setCurrentMode(mode);
    }
  }, [act?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Track whether user explicitly changed costRange dropdown (Rule 3)
  const costRangeManualRef = useRef(false);

  const [aiMode, setAiMode] = useState("hidden");
  const [aiPrompt, setAiPrompt] = useState("");
  const [aiPreview, setAiPreview] = useState(null);
  const [aiError, setAiError] = useState("");

  const dismiss = useCallback(() => {
    setClosing(true);
    setTimeout(onClose, 260);
  }, [onClose]);

  const set = key => e => {
    const val = e.target.value;
    if (key === "cost") {
      // Rule 2: user changed cost → auto-derive costRange (unless costRange was manually set)
      if (!costRangeManualRef.current) {
        const numeric = parseFloat(val);
        setForm(f => ({ ...f, cost: val, costRange: getCostRange(isNaN(numeric) ? null : numeric) }));
        return;
      }
    }
    if (key === "costRange") {
      // Rule 3: user explicitly set costRange → mark as manual
      costRangeManualRef.current = true;
    }
    setForm(f => ({ ...f, [key]: val }));
  };

  async function commitSave(data, toastMessage = "✅ Activity saved") {
    setSaving(true);
    try {
      const normalized = { ...data };
      if (!normalized.category) {
        const m = COST_TYPE_MAP[normalized.costType || act.costType] || {};
        if (!normalized.category) normalized.category = m.category || "sightseeing";
      }
      // Form path sends locationName/locationAddr strings; AI path sends a location object.
      const { locationName, locationAddr, location: locationObj, ...rest } = normalized;
      const updates = {
        ...rest,
        photo: latestPhotoRef.current ?? rest.photo,
        location: {
          ...(act.location || {}),
          ...(locationObj || {}),
          name:    locationName ?? locationObj?.name    ?? act.location?.name    ?? "",
          address: locationAddr ?? locationObj?.address ?? act.location?.address ?? "",
        },
        previousVersion: (() => { const { previousVersion: _prev, ...actWithoutPrev } = act; return actWithoutPrev; })(),
        version: (act.version || 1) + 1,
        // Fix 5: enforce fields for new activities
        ...(isNew ? { aiGenerated: false, planType: "primary", locked: true, ownerUid: auth.currentUser.uid } : {}),
      };
      // Ensure costRange is always written: derive from cost if not present (AI path)
      if (!updates.costRange) {
        const numeric = parseFloat(updates.cost);
        updates.costRange = getCostRange(isNaN(numeric) ? null : numeric);
      }
      // Fix 1: strip undefined values — Firestore rejects them
      const cleanPayload = Object.fromEntries(
        Object.entries(updates).filter(([_, v]) => v !== undefined)
      );
      await setDoc(doc(db, "activities", act.id), cleanPayload, { merge: true });
      latestPhotoRef.current = null;
      onActivityUpdate?.(act.id, updates);
      setSaved(true);
      showToast(toastMessage, "success");
      onSave({ ...act, ...updates });
      setTimeout(() => { setCurrentMode("view"); setSaving(false); setSaved(false); }, 900);
    } catch (e) {
      console.error("[commitSave] Firestore write failed:", e);
      setSaving(false);
      showToast("Failed to save — " + e.message, "error");
    }
  }

  const handleSave = async () => {
    if (saving || saved) return;
    if (!form.title?.trim()) { showToast("Please add a title before saving", "error"); return; }
    takeSnapshot(db, act.tripId, `Activity edited: ${form.title || ""}`.slice(0, 40), [], []).catch(err => console.error("Snapshot failed:", err));
    commitSave(form, isNew ? "✅ Activity added" : "✅ Activity saved");
  };
  const handleAcceptAI = async () => {
    if (!saving && !saved) {
      takeSnapshot(db, act.tripId, `AI Rewrite: ${aiPreview?.title || act.title || ""}`.slice(0, 40), [], []).catch(err => console.error("Snapshot failed:", err));
      commitSave(aiPreview, "✅ Activity rewritten by AI");
    }
  };

  const handleAIRewrite = async () => {
    if (!aiPrompt.trim() || aiMode === "loading") return;
    setAiMode("loading");
    setAiError("");
    // sanitized — control chars stripped, capped at 500
    const safePrompt = aiPrompt
      .trim()
      // eslint-disable-next-line no-control-regex
      .replace(/[\u0000-\u001F\u007F]/g, ' ')
      .slice(0, 500);
    const otherTitles = otherActivityTitles;
    const cities = tripCities.join(", ");
    const editableFields = {
      title:       form.title,
      detail:      form.detail,
      time:        form.time,
      duration:    form.duration,
      cost:        form.cost,
      costDetail:  form.costDetail,
      category:    form.category,
      location:    act.location || {},
      bookingUrl:  form.bookingUrl,
      bookingNote: form.bookingNote,
      alt:         act.alt || null,
    };
    const tripTitle = tripData?.title || "";
    const tripTravelers = tripData?.brief?.travelers?.join(", ") || tripData?.participants?.join(", ") || "";
    const tripStyle = Array.isArray(tripData?.brief?.style)
      ? tripData.brief.style.join(', ')
      : (tripData?.brief?.style || "");
    const tripBudget = tripData?.brief?.budget || "";

    const messageParts = [
      "Current activity (editable fields only):",
      JSON.stringify(editableFields, null, 2),
      "",
      `User request: ${safePrompt}`,
      "",
      `Trip: ${tripTitle} · ${tripTravelers} · Style: ${tripStyle} · Budget: ${tripBudget} · Cities: ${cities}`,
    ];
    if (tripData?.brief) {
      messageParts.push(`Must-sees: ${tripData.brief.mustSees || 'none'} · Avoid: ${tripData.brief.avoid || 'none'} (Items prefixed "Declined suggestion:" are specific rejected activities — avoid only that exact activity, not the broader category) · Departure: ${tripData.brief.departureCity || 'unspecified'}`);
    }
    if (day?.city) {
      messageParts.push(`This activity is in: ${day.city}, ${day?.country || ''} on ${day?.date || ''}`);
    }
    messageParts.push(
      "",
      "Other activities on this trip (avoid duplicates):",
      otherTitles.map(t => `- ${t}`).join("\n"),
      "",
      "Preserve exactly (do not modify): id, tripId, dayId, order, planType, notes, confirmed, locked, aiGenerated, version, previousVersion, photo.",
      "Only modify fields the user's request requires changing.",
    );
    const userMessage = messageParts.join("\n");

    try {
      let actRewriteSystem = "You are a travel planning assistant. Return ONLY a valid JSON object — no markdown, no backticks, no explanation. Use \"category\" (transport, accommodation, dining, culture, nature, leisure, shopping, nightlife). Return a numeric \"cost\" field (estimated per-person cost in USD). For bookingUrl, provide only the homepage domain (e.g. https://restaurant-kontrast.no), never a deep link. For bookingNote, include 2-3 word search terms the user can use to find the specific listing (e.g. 'Kontrast Oslo reservation'). Always include a location object with name, address, lat, lng, and placeId using real coordinates.";
      if (day?.budgetOverride) {
        actRewriteSystem += `\nBudget guidance for this day: ${day.budgetOverride}. Adjust activity cost levels accordingly — 'budget' means prioritize free/cheap options, 'moderate' means mid-range, 'splurge' means premium experiences.`;
      }
      if (day?.pace) {
        actRewriteSystem += `\nPace for this day: ${day.pace}. 'relaxed' means fewer activities with more downtime, 'moderate' means a balanced day, 'packed' means maximize activities and minimize gaps.`;
      }
      const res = await authenticatedFetch(`${API_URL}/api/claude`, {
        method: "POST",
        body: JSON.stringify({
          system: actRewriteSystem,
          messages: [{ role: "user", content: userMessage }],
          language: getSetting('wanderologie_settings.language') || 'English',
          model: getSetting('wanderologie_settings.aiModel'),
        }),
      });
      if (res.status === 429) {
        const errData = await res.json();
        showToast(errData.message, "error");
        return;
      }
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error?.message || `API error ${res.status}`);
      }
      const data = await res.json();
      const raw = (data.content?.[0]?.text || "").trim()
        .replace(/^```json?\n?/, "").replace(/```$/, "").trim();
      const parsed = JSON.parse(raw);
      // Whitelist allowed AI content fields — prevent AI from overwriting structural fields
      const ALLOWED_AI_FIELDS = [
        "title", "detail", "duration", "cost", "costDetail", "costNote",
        "category", "bookingStatus", "bookingLeadDays", "bookingUrl", "bookingNote",
        "startTime", "endTime", "time", "timeBlock",
        "location", "locationName", "locationAddr", "img", "alt",
      ];
      const safeAIResponse = {};
      for (const key of ALLOWED_AI_FIELDS) {
        if (parsed[key] !== undefined) safeAIResponse[key] = parsed[key];
      }
      // Log-only dedup check against sibling activities on the same day
      if (safeAIResponse.title && isDuplicate(safeAIResponse.title, otherTitles)) {
        console.log(`[DEDUP] Activity rewrite near-dupe: "${safeAIResponse.title}"`);
      }
      setAiPreview(safeAIResponse);
      setAiMode("preview");
    } catch (e) {
      if (e.message.includes("declined") || e.message.includes("refusal")) {
        showToast("✦ Couldn't generate — try rephrasing your request", "error");
        setAiMode("prompt");
      } else {
        setAiError(e.message);
        setAiMode("error");
      }
    }
  };

  return (
    <>
      <Toast message={toast.message} type={toast.type} visible={toast.visible} />

      {/* Lightbox */}
      {lightboxOpen && form.img && (
        <div
          onClick={() => setLightboxOpen(false)}
          style={{
            position: "fixed", inset: 0, zIndex: 600,
            background: "rgba(0,0,0,0.85)",
            display: "flex", alignItems: "center", justifyContent: "center",
            animation: "fadeIn 0.18s ease",
          }}
        >
          <button
            onClick={() => setLightboxOpen(false)}
            style={{
              position: "absolute", top: 14, right: 14,
              background: "rgba(255,255,255,0.15)", border: "1px solid rgba(255,255,255,0.2)",
              borderRadius: 10, color: COLOR.white, width: 44, height: 44,
              cursor: "pointer", fontSize: 16,
              display: "flex", alignItems: "center", justifyContent: "center",
            }}
          >
            <Icon name="close" size={16} color={COLOR.white} />
          </button>
          <img
            src={form.img}
            alt="Activity"
            onClick={e => e.stopPropagation()}
            style={{
              maxWidth: "90vw", maxHeight: "90vh",
              objectFit: "contain",
              objectPosition: form.imgCropParams
                ? `${form.imgCropParams.x}% ${form.imgCropParams.y}%`
                : "center center",
              borderRadius: 8,
            }}
          />
        </div>
      )}

      <div
        data-testid="activity-panel"
        onClick={dismiss}
        style={{
          position: "fixed", inset: 0, zIndex: 300,
          display: "flex",
          alignItems: isDesktop ? "stretch" : "stretch",
          justifyContent: isDesktop ? "flex-end" : "center",
          flexDirection: isDesktop ? "row" : "column",
          background: closing ? "rgba(44,24,16,0)" : "rgba(44,24,16,0.5)",
          transition: "background 0.26s",
        }}
      >
        <div
          style={{ position: "relative", display: "contents" }}
          onClick={e => e.stopPropagation()}
        >
          <div
            style={{
              position: "relative",
              background: COLOR.blush,
              borderRadius: isDesktop ? 0 : "24px 24px 0 0",
              width: isDesktop ? resolvedWidth : "100%",
              maxWidth: isDesktop ? resolvedWidth : "100%",
              height: isDesktop ? "100vh" : undefined,
              maxHeight: isDesktop ? "100vh" : "100dvh",
              overflowY: "auto",
              WebkitOverflowScrolling: "touch",
              paddingBottom: isDesktop ? 24 : "max(16px, env(safe-area-inset-bottom))",
              animation: `${closing ? (isDesktop ? "slideOutRight" : "slideDown") : (isDesktop ? "slideInRight" : "slideUp")} 0.26s cubic-bezier(0.32,0.72,0,1) forwards`,
              boxShadow: isDesktop ? "0 0 60px rgba(44,24,16,0.25)" : "none",
            }}
          >
            {/* Desktop resize handle */}
            {isDesktop && (
              <div
                onMouseDown={handleResizeDragStart}
                onDoubleClick={handleResizeDoubleClick}
                style={{
                  position: "absolute",
                  left: 0,
                  top: 0,
                  bottom: 0,
                  width: 6,
                  cursor: "ew-resize",
                  background: "transparent",
                  zIndex: 10,
                }}
              />
            )}
            {/* Mobile drag handle */}
            {!isDesktop && (
              <div style={{ display: "flex", justifyContent: "center", padding: "12px 0 4px" }}>
                <div style={{ width: 36, height: 4, borderRadius: 2, background: COLOR.borderWarm }} />
              </div>
            )}

            {/* Header */}
            {currentMode === "view" ? (
              <div
                data-testid="activity-panel-header"
                style={{
                padding: isDesktop ? "18px 20px 14px" : "10px 20px 14px",
                borderBottom: `1px solid ${COLOR.borderWarm}`,
              }}>
                {/* Row 1: Title + Edit + Close */}
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                  <h2 style={{ margin: 0, fontSize: 20, fontWeight: 400, color: COLOR.inkDark, lineHeight: 1.2, fontFamily: FONT.serif, flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {act.title}
                  </h2>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0, marginLeft: 12 }}>
                    <button
                      data-testid="activity-edit-button"
                      onClick={() => setCurrentMode("edit")}
                      style={{
                        background: COLOR.buttonSecondary, border: `1px solid ${COLOR.borderWarm}`,
                        borderRadius: 10, color: COLOR.inkMid, height: 44,
                        padding: "0 14px", cursor: "pointer", fontSize: 13,
                        fontFamily: FONT.mono, letterSpacing: "0.04em",
                        display: "flex", alignItems: "center", gap: 6,
                      }}
                    >
                      <Icon name="edit" size={14} color={COLOR.inkMid} /> Edit
                    </button>
                    <button
                      data-testid="activity-close-button"
                      onClick={dismiss}
                      style={{ background: COLOR.buttonSecondary, border: `1px solid ${COLOR.borderWarm}`, borderRadius: 10, color: COLOR.inkMid, width: 44, height: 44, cursor: "pointer", fontSize: 16, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}
                    >
                      <Icon name="close" size={16} color={COLOR.inkMid} />
                    </button>
                  </div>
                </div>
                {/* Row 2: Badges (left) + Approve (right) */}
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
                  <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                    {CATEGORIES[act.category] && (
                      <span style={{
                        display: "inline-flex", alignItems: "center", gap: 4,
                        fontSize: 11, fontFamily: FONT.mono, letterSpacing: "0.06em",
                        color: COLOR.categoryBadgeText, background: COLOR.categoryBadgeBg,
                        padding: "4px 10px", borderRadius: 10,
                      }}>
                        <Icon name={CATEGORIES[act.category].iconName} size={13} color={COLOR.categoryBadgeText} /> {CATEGORIES[act.category].label}
                      </span>
                    )}
                    <span style={{
                      fontSize: 11, fontFamily: FONT.mono, letterSpacing: "0.06em",
                      color: COLOR.mauve, background: `${COLOR.mauve}12`,
                      padding: "4px 10px", borderRadius: 10,
                    }}>
                      {act.time || "Morning"}
                    </span>
                    {act.duration && (
                      <span style={{
                        fontSize: 11, fontFamily: FONT.mono, color: COLOR.inkMid,
                        background: COLOR.categoryBadgeBg, padding: "4px 10px", borderRadius: 10,
                      }}>
                        {act.duration}
                      </span>
                    )}
                  </div>
                  <button
                    onClick={handleToggleLock}
                    disabled={lockSaving}
                    title={locked ? "Unapprove activity" : "Approve activity"}
                    style={{
                      background: locked ? `${COLOR.lockedGreen}12` : COLOR.buttonSecondary,
                      border: `1px solid ${locked ? COLOR.lockedGreen : COLOR.borderWarm}`,
                      borderRadius: 10, color: locked ? COLOR.lockedGreen : COLOR.inkMid,
                      padding: "0 12px", height: 36, cursor: lockSaving ? "default" : "pointer",
                      fontSize: 13, fontFamily: FONT.mono, letterSpacing: "0.04em",
                      display: "flex", alignItems: "center", gap: 5, flexShrink: 0,
                      opacity: lockSaving ? 0.6 : 1, transition: "all 0.15s",
                    }}
                  >
                    <Icon name="confirm" size={13} color={locked ? COLOR.lockedGreen : COLOR.inkMid} style={{ marginRight: 4 }} /> {locked ? "Approved" : "Unapproved"}
                  </button>
                </div>
              </div>
            ) : (
              <div style={{
                display: "flex", justifyContent: "space-between", alignItems: "center",
                padding: isDesktop ? "18px 20px 14px" : "10px 20px 14px",
                borderBottom: `1px solid ${COLOR.borderWarm}`,
              }}>
                <div>
                  <div style={{ fontSize: 10, color: COLOR.mauve, fontFamily: FONT.mono, letterSpacing: "0.12em", marginBottom: 3 }}>
                    {aiMode === "preview"
                      ? <><Icon name="rewrite" size={12} color={COLOR.mauve} style={{ marginRight: 4, verticalAlign: "middle" }} /> AI REWRITE PREVIEW</>
                      : <><Icon name="edit" size={12} color={COLOR.mauve} style={{ marginRight: 4, verticalAlign: "middle" }} /> EDITING ACTIVITY</>}
                  </div>
                  <div style={{ fontSize: 16, color: COLOR.inkDark, fontFamily: FONT.serif, lineHeight: 1.2 }}>
                    {act.title}
                  </div>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
                  <button
                    onClick={handleToggleLock}
                    disabled={lockSaving}
                    title={locked ? "Unapprove activity" : "Approve activity"}
                    style={{
                      background: locked ? `${COLOR.lockedGreen}12` : COLOR.buttonSecondary,
                      border: `1px solid ${locked ? COLOR.lockedGreen : COLOR.borderWarm}`,
                      borderRadius: 10, color: locked ? COLOR.lockedGreen : COLOR.inkMid,
                      padding: "0 12px", height: 44, cursor: lockSaving ? "default" : "pointer",
                      fontSize: 13, fontFamily: FONT.mono, letterSpacing: "0.04em",
                      display: "flex", alignItems: "center", gap: 5,
                      opacity: lockSaving ? 0.6 : 1, transition: "all 0.15s",
                    }}
                  >
                    <Icon name="confirm" size={13} color={locked ? COLOR.lockedGreen : COLOR.inkMid} style={{ marginRight: 4 }} /> {locked ? "Approved" : "Unapproved"}
                  </button>
                  <button
                    onClick={dismiss}
                    style={{ background: COLOR.buttonSecondary, border: `1px solid ${COLOR.borderWarm}`, borderRadius: 10, color: COLOR.inkMid, width: 44, height: 44, cursor: "pointer", fontSize: 16, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}
                  >
                    <Icon name="close" size={16} color={COLOR.inkMid} />
                  </button>
                </div>
              </div>
            )}

            {currentMode === "view" ? (
              <ViewDisplay act={act} isDesktop={isDesktop} showBooking={showBooking} showCost={showCost} showLocation={showLocation} />
            ) : aiMode === "preview" ? (
              <PreviewPanel
                act={act}
                form={form}
                preview={aiPreview}
                saving={saving}
                saved={saved}
                onAccept={handleAcceptAI}
                onTryAgain={() => { setAiMode("prompt"); setAiPreview(null); }}
                onCancel={dismiss}
              />
            ) : (
              <EditForm
                form={form} set={set}
                act={act}
                isDesktop={isDesktop}
                showImageEditor={showImageEditor}
                setShowImageEditor={setShowImageEditor}
                onOpenLightbox={() => setLightboxOpen(true)}
                onImageSave={({ url, cropParams }) => {
                  latestPhotoRef.current = url;
                  setForm(f => ({ ...f, img: url, imgCropParams: cropParams || null }));
                  setShowImageEditor(false);
                }}
                aiMode={aiMode} aiPrompt={aiPrompt} aiError={aiError}
                setAiPrompt={setAiPrompt} setAiMode={setAiMode} setAiError={setAiError}
                onAIRewrite={handleAIRewrite}
                saving={saving} saved={saved} isNew={isNew}
                onSave={handleSave} onCancel={() => setCurrentMode("view")}
                onDeleteActivity={onDeleteActivity} actTitle={act.title} actId={act.id}
                showBooking={showBooking} showCost={showCost} showLocation={showLocation}
                day={day}
              />
            )}
          </div>
        </div>
      </div>
    </>
  );
}

// ── Edit form ────────────────────────────────────────────────────────────────

function EditForm({ form, set, act, isDesktop, showImageEditor, setShowImageEditor, onOpenLightbox, onImageSave, aiMode, aiPrompt, aiError, setAiPrompt, setAiMode, setAiError, onAIRewrite, saving, saved, isNew, onSave, onCancel, onDeleteActivity, actTitle, actId, showBooking, showCost, showLocation, day }) {
  const [confirmDelete, setConfirmDelete] = useState(false);
  const imgObjectPosition = form.imgCropParams
    ? `${form.imgCropParams.x}% ${form.imgCropParams.y}%`
    : undefined;

  return (
    <div style={{ padding: "20px 20px 8px", paddingBottom: 80 }}>

      {/* Two-column layout: image left, fields right (desktop only) */}
      <div style={{
        display: "flex",
        flexDirection: isDesktop ? "row" : "column",
        alignItems: isDesktop ? "stretch" : "flex-start",
        gap: isDesktop ? 24 : 0,
        marginBottom: 16,
      }}>
        {/* Left column: image */}
        <div style={{ flex: isDesktop ? "0 0 calc(50% - 12px)" : undefined, width: isDesktop ? undefined : "100%", display: "flex", flexDirection: "column" }}>
          <label style={labelStyle}>ACTIVITY IMAGE</label>
          {form.img && (
            <div
              onClick={onOpenLightbox}
              style={{ marginBottom: 8, borderRadius: 10, overflow: "hidden", border: `1px solid ${COLOR.borderWarm}`, cursor: "zoom-in" }}
            >
              <Img
                src={form.img}
                alt="Activity"
                style={{ width: "100%", aspectRatio: "16/9" }}
                objectPosition={imgObjectPosition}
              />
            </div>
          )}
          {showImageEditor ? (
            <div style={{ border: `1px solid ${COLOR.borderWarm}`, borderRadius: 12, padding: "16px", marginBottom: 8 }}>
              <ImageEditor
                currentUrl={form.img}
                aspectRatio={4 / 3}
                tripId={act.tripId}
                onSave={onImageSave}
                onCancel={() => setShowImageEditor(false)}
                activityTitle={act.title}
                city={day?.city || ""}
                activityLocation={act.location?.name || ""}
                context="activity"
              />
            </div>
          ) : (
            <button
              onClick={() => setShowImageEditor(true)}
              style={{
                width: "100%", background: `linear-gradient(135deg, ${COLOR.blushLight}, ${COLOR.white})`,
                border: `1px solid ${COLOR.mauve}44`, borderRadius: 12,
                color: COLOR.mauve, padding: "11px", fontSize: 13,
                fontFamily: FONT.mono, cursor: "pointer", letterSpacing: "0.04em",
                display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                minHeight: 44,
                marginTop: isDesktop ? "auto" : undefined,
                marginBottom: isDesktop ? 0 : 16,
              }}
            >
              <Icon name="changeImage" size={14} color={COLOR.mauve} style={{ marginRight: 4 }} /> {form.img ? "Change Image" : "Add Image"}
            </button>
          )}
        </div>

        {/* Right column: title through category (+ location & AI on desktop) */}
        <div style={{ flex: isDesktop ? "0 0 calc(50% - 12px)" : undefined, width: isDesktop ? undefined : "100%", display: "flex", flexDirection: "column" }}>
          {/* Title */}
          <div style={{ marginBottom: 16 }}>
            <label style={labelStyle}>TITLE</label>
            <input value={form.title} onChange={set("title")} onFocus={focus} onBlur={blur} style={inputStyle} />
          </div>

          {/* Time block + Duration */}
          <div style={{ display: "flex", gap: 10, marginBottom: 16, flexWrap: "wrap" }}>
            <div style={{ flex: 1, minWidth: 140 }}>
              <label style={labelStyle}>TIME BLOCK</label>
              <select value={form.time} onChange={set("time")} onFocus={focus} onBlur={blur} style={selectStyle}>
                {TIME_OPTIONS.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div style={{ flex: 1, minWidth: 140 }}>
              <label style={labelStyle}>DURATION</label>
              <input value={form.duration} onChange={set("duration")} onFocus={focus} onBlur={blur}
                placeholder="e.g. 2–3 hours" style={inputStyle} />
            </div>
          </div>

          {/* Cost + Cost range */}
          <div style={{ display: "flex", gap: 10, marginBottom: 16, flexWrap: "wrap" }}>
            {showCost && (
            <div style={{ flex: 1, minWidth: 140 }}>
              <label style={labelStyle}>COST</label>
              <input value={form.cost} onChange={set("cost")} onFocus={focus} onBlur={blur}
                placeholder="e.g. ~$25pp" style={inputStyle} />
            </div>
            )}
            <div style={{ flex: 1, minWidth: 140 }}>
              <label style={labelStyle}>COST RANGE</label>
              <select value={form.costRange} onChange={set("costRange")} onFocus={focus} onBlur={blur} style={selectStyle}>
                {COST_RANGE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
          </div>

          {/* Category */}
          <div style={{ marginBottom: 16 }}>
            <label style={labelStyle}>CATEGORY</label>
            <select value={form.category} onChange={set("category")} onFocus={focus} onBlur={blur} style={selectStyle}>
              {CATEGORY_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>

          {/* Location — desktop only (inside right column) */}
          {isDesktop && showLocation && (
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 10, color: COLOR.inkMid, fontFamily: FONT.mono, letterSpacing: "0.1em", marginBottom: 10, opacity: 0.7 }}>
                LOCATION
              </div>
              <div style={{ display: "flex", gap: 10 }}>
                <div style={{ flex: 1 }}>
                  <label style={{ ...labelStyle, opacity: 0.7 }}>NAME</label>
                  <input value={form.locationName} onChange={set("locationName")} onFocus={focus} onBlur={blur}
                    placeholder="e.g. Maaemo" style={{ ...inputStyle, fontSize: 13 }} />
                </div>
                <div style={{ flex: 2 }}>
                  <label style={{ ...labelStyle, opacity: 0.7 }}>ADDRESS</label>
                  <input data-testid="activity-address-input" value={form.locationAddr} onChange={set("locationAddr")} onFocus={focus} onBlur={blur}
                    placeholder="e.g. 123 Main St, City" style={{ ...inputStyle, fontSize: 13 }} />
                </div>
              </div>
            </div>
          )}

          {/* AI Rewrite — desktop only (pushed to bottom of right column) */}
          {isDesktop && (
            <div style={{ marginTop: "auto" }}>
              {aiMode === "hidden" && (
                <button
                  onClick={() => setAiMode("prompt")}
                  style={{
                    width: "100%", background: `linear-gradient(135deg, ${COLOR.blushLight}, ${COLOR.white})`,
                    border: `1px solid ${COLOR.mauve}44`, borderRadius: 12,
                    color: COLOR.mauve, padding: "13px", fontSize: 14,
                    fontFamily: FONT.mono, cursor: "pointer", letterSpacing: "0.04em",
                    display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                    minHeight: 48,
                  }}
                >
                  <Icon name="rewrite" size={14} color={COLOR.mauve} style={{ marginRight: 4, verticalAlign: "middle" }} /> Rewrite with AI
                </button>
              )}
              {(aiMode === "prompt" || aiMode === "loading" || aiMode === "error") && (
                <div>
                  <label style={labelStyle}><Icon name="rewrite" size={12} color={COLOR.mauve} style={{ marginRight: 4, verticalAlign: "middle" }} /> AI REWRITE PROMPT</label>
                  <textarea
                    value={aiPrompt}
                    onChange={e => setAiPrompt(e.target.value)}
                    onFocus={focus} onBlur={blur}
                    placeholder="e.g. I'd rather do a slow morning brunch instead"
                    rows={3}
                    disabled={aiMode === "loading"}
                    style={{ ...inputStyle, resize: "vertical", lineHeight: 1.6, marginBottom: 10, opacity: aiMode === "loading" ? 0.6 : 1 }}
                  />
                  {aiError && (
                    <div style={{ fontSize: 12, color: COLOR.mauveDark, fontFamily: FONT.mono, marginBottom: 10, padding: "8px 12px", background: COLOR.altCardGradient, borderRadius: 8, border: `1px solid ${COLOR.mauve}33` }}>
                      ⚠ {aiError}
                    </div>
                  )}
                  <div style={{ display: "flex", gap: 8 }}>
                    <button
                      onClick={onAIRewrite}
                      disabled={aiMode === "loading" || !aiPrompt.trim()}
                      style={{
                        flex: 2, border: "none", borderRadius: 10,
                        background: aiMode === "loading" ? COLOR.borderWarm : COLOR.mauve,
                        color: aiMode === "loading" ? COLOR.inkMid : COLOR.white,
                        padding: "11px", fontSize: 13, fontFamily: FONT.mono,
                        cursor: aiMode === "loading" || !aiPrompt.trim() ? "default" : "pointer",
                        minHeight: 44,
                      }}
                    >
                      <Icon name="rewrite" size={14} color={aiMode === "loading" ? COLOR.inkMid : COLOR.white} style={{ marginRight: 4, verticalAlign: "middle" }} /> {aiMode === "loading" ? "Thinking…" : "Rewrite"}
                    </button>
                    <button
                      onClick={() => { setAiMode("hidden"); setAiPrompt(""); setAiError(""); }}
                      style={{
                        flex: 1, background: COLOR.buttonSecondary, border: `1px solid ${COLOR.borderWarm}`,
                        borderRadius: 10, color: COLOR.inkMid, padding: "11px",
                        cursor: "pointer", fontSize: 13, fontFamily: FONT.mono, minHeight: 44,
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Cost detail */}
      {showCost && (
      <div style={{ marginBottom: 16 }}>
        <label style={labelStyle}>COST DETAIL</label>
        <textarea value={form.costDetail} onChange={set("costDetail")} onFocus={focus} onBlur={blur}
          rows={2} style={{ ...inputStyle, resize: "vertical", lineHeight: 1.6 }} />
      </div>
      )}

      {/* Description */}
      <div style={{ marginBottom: 16 }}>
        <label style={labelStyle}>DESCRIPTION</label>
        <textarea value={form.detail} onChange={set("detail")} onFocus={focus} onBlur={blur}
          rows={4} style={{ ...inputStyle, resize: "vertical", lineHeight: 1.7 }} />
      </div>

      {/* Booking URL */}
      {showBooking && (
      <div style={{ marginBottom: 16 }}>
        <label style={labelStyle}>BOOKING URL</label>
        <input value={form.bookingUrl} onChange={set("bookingUrl")} onFocus={focus} onBlur={blur}
          placeholder="https://…" style={inputStyle} />
      </div>
      )}

      {/* Booking note */}
      {showBooking && (
      <div style={{ marginBottom: 20 }}>
        <label style={labelStyle}>BOOKING NOTE</label>
        <textarea value={form.bookingNote} onChange={set("bookingNote")} onFocus={focus} onBlur={blur}
          rows={2} style={{ ...inputStyle, resize: "vertical", lineHeight: 1.6 }} />
      </div>
      )}

      {/* Location — mobile only (desktop renders inside right column) */}
      {!isDesktop && showLocation && (
        <div style={{ borderTop: `1px solid ${COLOR.borderWarm}`, paddingTop: 16, marginBottom: 16 }}>
          <div style={{ fontSize: 10, color: COLOR.inkMid, fontFamily: FONT.mono, letterSpacing: "0.1em", marginBottom: 10, opacity: 0.7 }}>
            LOCATION
          </div>
          <div style={{ display: "flex", gap: 10, flexDirection: "column" }}>
            <div style={{ flex: 1 }}>
              <label style={{ ...labelStyle, opacity: 0.7 }}>NAME</label>
              <input value={form.locationName} onChange={set("locationName")} onFocus={focus} onBlur={blur}
                placeholder="e.g. Maaemo" style={{ ...inputStyle, fontSize: 13 }} />
            </div>
            <div style={{ flex: 1 }}>
              <label style={{ ...labelStyle, opacity: 0.7 }}>ADDRESS</label>
              <input value={form.locationAddr} onChange={set("locationAddr")} onFocus={focus} onBlur={blur}
                placeholder="e.g. 123 Main St, City" style={{ ...inputStyle, fontSize: 13 }} />
            </div>
          </div>
        </div>
      )}

      {/* AI Rewrite — mobile only (desktop renders inside right column) */}
      {!isDesktop && (
        <div style={{ borderTop: `1px solid ${COLOR.borderWarm}`, paddingTop: 18, marginBottom: 16 }}>
          {aiMode === "hidden" && (
            <button
              onClick={() => setAiMode("prompt")}
              style={{
                width: "100%", background: `linear-gradient(135deg, ${COLOR.blushLight}, ${COLOR.white})`,
                border: `1px solid ${COLOR.mauve}44`, borderRadius: 12,
                color: COLOR.mauve, padding: "13px", fontSize: 14,
                fontFamily: FONT.mono, cursor: "pointer", letterSpacing: "0.04em",
                display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                minHeight: 48,
              }}
            >
              <Icon name="rewrite" size={14} color={COLOR.mauve} style={{ marginRight: 4, verticalAlign: "middle" }} /> Rewrite with AI
            </button>
          )}
          {(aiMode === "prompt" || aiMode === "loading" || aiMode === "error") && (
            <div>
              <label style={labelStyle}><Icon name="rewrite" size={12} color={COLOR.mauve} style={{ marginRight: 4, verticalAlign: "middle" }} /> AI REWRITE PROMPT</label>
              <textarea
                value={aiPrompt}
                onChange={e => setAiPrompt(e.target.value)}
                onFocus={focus} onBlur={blur}
                placeholder="e.g. I'd rather do a slow morning brunch instead"
                rows={3}
                disabled={aiMode === "loading"}
                style={{ ...inputStyle, resize: "vertical", lineHeight: 1.6, marginBottom: 10, opacity: aiMode === "loading" ? 0.6 : 1 }}
              />
              {aiError && (
                <div style={{ fontSize: 12, color: COLOR.mauveDark, fontFamily: FONT.mono, marginBottom: 10, padding: "8px 12px", background: COLOR.altCardGradient, borderRadius: 8, border: `1px solid ${COLOR.mauve}33` }}>
                  ⚠ {aiError}
                </div>
              )}
              <div style={{ display: "flex", gap: 8 }}>
                <button
                  onClick={onAIRewrite}
                  disabled={aiMode === "loading" || !aiPrompt.trim()}
                  style={{
                    flex: 2, border: "none", borderRadius: 10,
                    background: aiMode === "loading" ? COLOR.borderWarm : COLOR.mauve,
                    color: aiMode === "loading" ? COLOR.inkMid : COLOR.white,
                    padding: "11px", fontSize: 13, fontFamily: FONT.mono,
                    cursor: aiMode === "loading" || !aiPrompt.trim() ? "default" : "pointer",
                    minHeight: 44,
                  }}
                >
                  <Icon name="rewrite" size={14} color={aiMode === "loading" ? COLOR.inkMid : COLOR.white} style={{ marginRight: 4, verticalAlign: "middle" }} /> {aiMode === "loading" ? "Thinking…" : "Rewrite"}
                </button>
                <button
                  onClick={() => { setAiMode("hidden"); setAiPrompt(""); setAiError(""); }}
                  style={{
                    flex: 1, background: COLOR.buttonSecondary, border: `1px solid ${COLOR.borderWarm}`,
                    borderRadius: 10, color: COLOR.inkMid, padding: "11px",
                    cursor: "pointer", fontSize: 13, fontFamily: FONT.mono, minHeight: 44,
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Version info */}
      <div style={{ fontSize: 10, color: COLOR.inkMid, fontFamily: FONT.mono, opacity: 0.5, marginBottom: 14, textAlign: "right" }}>
        v{act.version || 1} · {act.id}
      </div>

      {/* Delete Activity */}
      {onDeleteActivity && !isNew && (
        !confirmDelete ? (
          <button
            onClick={() => setConfirmDelete(true)}
            style={{
              width: "100%", marginBottom: 8,
              background: "transparent", border: `1px solid ${COLOR.borderWarm}33`,
              borderRadius: 10, color: COLOR.inkMid, fontSize: 13,
              fontFamily: FONT.mono, padding: "10px 14px",
              minHeight: 44, cursor: "pointer",
            }}
          >
            <Icon name="delete" size={15} color={COLOR.inkMid} style={{ marginRight: 4 }} /> Delete Activity
          </button>
        ) : (
          <div style={{ marginBottom: 8 }}>
            <div style={{ fontSize: 13, color: COLOR.mauveDark, fontFamily: FONT.mono, textAlign: "center", marginBottom: 8 }}>
              Delete {actTitle}? This cannot be undone.
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <button
                onClick={() => { onDeleteActivity(actId); }}
                style={{
                  flex: 1, background: COLOR.mauveDark, color: COLOR.white,
                  border: "none", borderRadius: 10, fontSize: 13,
                  fontFamily: FONT.mono, padding: "10px 14px",
                  minHeight: 44, cursor: "pointer",
                }}
              >
                Yes, delete
              </button>
              <button
                onClick={() => setConfirmDelete(false)}
                style={{
                  flex: 1, background: COLOR.buttonSecondary, border: `1px solid ${COLOR.borderWarm}`,
                  borderRadius: 10, color: COLOR.inkMid, fontSize: 13,
                  fontFamily: FONT.mono, padding: "10px 14px",
                  minHeight: 44, cursor: "pointer",
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        )
      )}

      {/* Sticky save bar */}
      {!showImageEditor && (
        <div style={{
          position: "sticky", bottom: 0,
          background: COLOR.blush,
          padding: "12px 0 env(safe-area-inset-bottom, 4px)", marginTop: 4, display: "flex", gap: 10,
        }}>
          <button
            data-testid="activity-save-button"
            onClick={onSave}
            disabled={saving || saved}
            style={{
              flex: 2, border: "none", borderRadius: 12, color: COLOR.white,
              padding: "14px", fontSize: 15, fontFamily: FONT.mono,
              minHeight: 52, cursor: saving || saved ? "default" : "pointer",
              background: saved ? COLOR.savedGreen : COLOR.mauve,
              transition: "background 0.2s",
            }}
          >
            {saved ? <>Saved <Icon name="confirm" size={14} color={COLOR.white} /></> : saving ? "Saving…" : isNew ? "Add Activity" : "Save Changes"}
          </button>
          <button
            data-testid="activity-cancel-button"
            onClick={onCancel}
            style={{
              flex: 1, background: COLOR.buttonSecondary, border: `1px solid ${COLOR.borderWarm}`,
              borderRadius: 12, color: COLOR.inkMid, padding: "14px",
              cursor: "pointer", fontSize: 15, fontFamily: FONT.mono, minHeight: 52,
            }}
          >
            Cancel
          </button>
        </div>
      )}
    </div>
  );
}

// ── View display (read-only detail) ───────────────────────────────────────────

const BOOKING_STATUS_LABELS = {
  not_needed: "Not needed",
  book_day_of: "Day of",
  book_far_ahead: "Book ahead",
  book_now: "Book now",
  booked: "Booked",
};

function ViewDisplay({ act, isDesktop, showBooking, showCost, showLocation }) {
  const imgObjectPosition = act.imgCropParams
    ? `${act.imgCropParams.x}% ${act.imgCropParams.y}%`
    : "center bottom";
  const costRange = act.costRange || getCostRange(act.cost);
  const costRangeLabel = COST_RANGE_OPTIONS.find(o => o.value === costRange)?.label || costRange;
  const bookingStatusLabel = BOOKING_STATUS_LABELS[act.bookingStatus] || "Not needed";

  return (
    <div data-testid="activity-view-display" style={{ padding: 0 }}>
      {/* Photo */}
      {(act.img || act.photo) && (
        <div style={{ overflow: "hidden" }}>
          <img
            src={act.img || act.photo}
            alt={act.title}
            style={{
              width: "100%", aspectRatio: "16/9",
              objectFit: "cover", objectPosition: imgObjectPosition,
              display: "block",
            }}
          />
        </div>
      )}

      <div style={{ padding: "18px 20px 20px" }}>
        {/* Description */}
        {act.detail ? (
          <p style={{ fontSize: 15, color: COLOR.inkMid, lineHeight: 1.8, margin: "0 0 18px" }}>
            {act.detail}
          </p>
        ) : (
          <p style={{ fontSize: 14, color: COLOR.inkLight, lineHeight: 1.6, margin: "0 0 18px", fontStyle: "italic" }}>
            No description
          </p>
        )}

        {/* Cost section */}
        <div data-testid="activity-cost-section" style={{ background: COLOR.warmSurface, border: `1px solid ${COLOR.borderWarm}`, borderRadius: 12, padding: "14px 16px", marginBottom: 14 }}>
          <div style={{ fontSize: 11, color: COLOR.mauve, fontFamily: FONT.mono, letterSpacing: "0.1em", marginBottom: 8 }}>COST</div>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            {showCost && (
              <span style={{ fontSize: 15, color: COLOR.inkDark, fontFamily: FONT.mono }}>
                {formatCost(act.cost) || "—"}
              </span>
            )}
            {showCost && !(formatCost(act.cost) === "Free" && (costRange === "free" || !act.costRange)) && (
              <span
                data-testid="activity-cost-badge"
                style={{
                fontSize: 11, fontFamily: FONT.mono, letterSpacing: "0.06em", fontWeight: 600,
                background: COLOR.categoryBadgeBg, color: COLOR.categoryBadgeText,
                padding: "4px 10px", borderRadius: 10,
              }}>
                {costRangeLabel}
              </span>
            )}
          </div>
          {showCost && act.costDetail && (
            <div style={{ fontSize: 13, color: COLOR.inkMid, lineHeight: 1.7, marginTop: 8 }}>{act.costDetail}</div>
          )}
        </div>

        {/* Booking section */}
        {showBooking && (
        <div data-testid="activity-booking-section" style={{ background: COLOR.warmSurface, border: `1px solid ${COLOR.borderWarm}`, borderRadius: 12, padding: "14px 16px", marginBottom: 14 }}>
          <div style={{ fontSize: 11, color: COLOR.mauve, fontFamily: FONT.mono, letterSpacing: "0.1em", marginBottom: 10 }}>
            <Icon name="clipboard" size={14} color={COLOR.mauve} style={{ marginRight: 4, verticalAlign: "middle" }} /> BOOKING
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginBottom: (act.bookingUrl || act.bookingNote || act.bookingLeadDays) ? 10 : 0 }}>
            <span style={{
              fontSize: 12, fontFamily: FONT.mono, fontWeight: 600,
              color: act.bookingStatus === "booked" ? COLOR.lockedGreen : COLOR.inkMid,
              background: act.bookingStatus === "booked" ? `${COLOR.lockedGreen}12` : COLOR.categoryBadgeBg, /* lockedGreen @ 7% */
              padding: "5px 12px", borderRadius: 8,
              border: `1px solid ${act.bookingStatus === "booked" ? COLOR.lockedGreen : COLOR.borderWarm}`,
            }}>
              {bookingStatusLabel}
            </span>
            {act.bookingLeadDays && (
              <span data-testid="activity-lead-days" style={{ fontSize: 12, fontFamily: FONT.mono, color: COLOR.inkMid }}>
                {act.bookingLeadDays} {act.bookingLeadDays === 1 ? 'day' : 'days'} ahead
              </span>
            )}
          </div>
          {act.bookingUrl && (
            <a
              href={act.bookingUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: "inline-flex", alignItems: "center", gap: 4,
                fontSize: 13, color: COLOR.mauve, fontFamily: FONT.mono,
                textDecoration: "none", marginBottom: act.bookingNote ? 8 : 0,
              }}
            >
              {act.bookingUrl.replace("https://", "").replace("www.", "").split("/")[0]} ↗
            </a>
          )}
          {act.bookingNote && (
            <div style={{ fontSize: 13, color: COLOR.inkMid, lineHeight: 1.7 }}>{act.bookingNote}</div>
          )}
        </div>
        )}

        {/* Location */}
        {showLocation && (act.location?.name || act.location?.address) && (
          <div data-testid="activity-location-section" style={{ background: COLOR.warmSurface, border: `1px solid ${COLOR.borderWarm}`, borderRadius: 12, padding: "14px 16px", marginBottom: 14 }}>
            <div style={{ fontSize: 11, color: COLOR.mauve, fontFamily: FONT.mono, letterSpacing: "0.1em", marginBottom: 8 }}>LOCATION</div>
            {act.location?.name && (
              <div style={{ fontSize: 14, color: COLOR.inkDark, fontFamily: FONT.serif, marginBottom: 2 }}>{act.location.name}</div>
            )}
            {act.location?.address && (
              <div style={{ fontSize: 13, color: COLOR.inkMid, lineHeight: 1.5 }}>{act.location.address}</div>
            )}
          </div>
        )}

        {/* Notes */}
        <div style={{ background: COLOR.white, border: `1px solid ${COLOR.borderWarm}`, borderRadius: 12, padding: "14px 16px", marginBottom: 14 }}>
          <div style={{ fontSize: 11, color: COLOR.inkMid, fontFamily: FONT.mono, letterSpacing: "0.1em", marginBottom: 8 }}>
            <Icon name="notes" size={14} color={COLOR.inkMid} style={{ marginRight: 4, verticalAlign: "middle" }} /> NOTES
          </div>
          <div style={{ fontSize: 14, color: act.notes ? COLOR.inkDark : COLOR.inkLight, fontFamily: FONT.serif, lineHeight: 1.6 }}>
            {act.notes || "No notes"}
          </div>
        </div>

        {/* Version info */}
        {process.env.NODE_ENV === 'development' && (
          <div data-testid="activity-debug-info" style={{ fontSize: 10, color: COLOR.inkMid, fontFamily: FONT.mono, opacity: 0.5, textAlign: "right" }}>
            v{act.version || 1} · {act.id}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Preview panel ────────────────────────────────────────────────────────────

function PreviewPanel({ act, form, preview, saving, saved, onAccept, onTryAgain, onCancel }) {
  const currentActivity = { ...act, ...form };
  const fields = [
    { key: "title",  label: "TITLE",       multiline: false },
    { key: "time",   label: "TIME BLOCK",  multiline: false },
    { key: "cost",   label: "COST",        multiline: false },
    { key: "detail", label: "DESCRIPTION", multiline: true  },
  ];

  return (
    <div style={{ padding: "18px 20px 8px" }}>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 4 }}>
        <div style={{ fontSize: 10, color: COLOR.inkMid, fontFamily: FONT.mono, letterSpacing: "0.1em", textAlign: "center", padding: "4px 0" }}>CURRENT</div>
        <div style={{ fontSize: 10, color: COLOR.savedGreen, fontFamily: FONT.mono, letterSpacing: "0.1em", textAlign: "center", padding: "4px 0" }}><Icon name="rewrite" size={10} color={COLOR.savedGreen} style={{ marginRight: 3, verticalAlign: "middle" }} /> PROPOSED</div>
      </div>
      {fields.map(({ key, label, multiline }) => {
        const curr = currentActivity[key] || "—";
        const prop = preview[key] || "—";
        const changed = curr !== prop;
        return (
          <div key={key} style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 10, color: COLOR.inkMid, fontFamily: FONT.mono, letterSpacing: "0.1em", marginBottom: 6 }}>{label}</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              <div style={{ background: COLOR.warmSurface, border: `1px solid ${COLOR.borderWarm}`, borderRadius: 8, padding: "10px 12px", fontSize: 13, color: COLOR.inkMid, fontFamily: FONT.serif, lineHeight: 1.55, ...(multiline ? {} : { whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }) }}>
                {curr}
              </div>
              <div style={{ background: changed ? COLOR.diffBg : COLOR.warmSurface, border: `1px solid ${changed ? COLOR.diffBorder : COLOR.borderWarm}`, borderRadius: 8, padding: "10px 12px", fontSize: 13, color: changed ? COLOR.inkDark : COLOR.inkMid, fontFamily: FONT.serif, lineHeight: 1.55, ...(multiline ? {} : { whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }) }}>
                {prop}
                {changed && <span style={{ display: "block", fontSize: 10, color: COLOR.savedGreen, fontFamily: FONT.mono, marginTop: 4, opacity: 0.8 }}>● changed</span>}
              </div>
            </div>
          </div>
        );
      })}
      <div style={{ display: "flex", gap: 10, marginTop: 8 }}>
        <button onClick={onAccept} disabled={saving || saved} style={{ flex: 2, border: "none", borderRadius: 12, color: COLOR.white, padding: "14px", fontSize: 15, fontFamily: FONT.mono, minHeight: 52, cursor: saving || saved ? "default" : "pointer", background: saved ? COLOR.savedGreen : COLOR.mauve, transition: "background 0.2s" }}>
          {saved ? <>Saved <Icon name="confirm" size={14} color={COLOR.white} /></> : saving ? "Saving…" : "Accept"}
        </button>
        <button onClick={onTryAgain} style={{ flex: 1, background: COLOR.buttonSecondary, border: `1px solid ${COLOR.borderWarm}`, borderRadius: 12, color: COLOR.inkMid, padding: "14px", cursor: "pointer", fontSize: 14, fontFamily: FONT.mono, minHeight: 52 }}>
          Try Again
        </button>
      </div>
      <button onClick={onCancel} style={{ width: "100%", marginTop: 10, background: "transparent", border: "none", color: COLOR.inkMid, fontSize: 13, fontFamily: FONT.mono, cursor: "pointer", padding: "8px" }}>
        Cancel
      </button>
    </div>
  );
}
